/**
 * User Analytics Service
 * API service for user usage analytics and metrics
 */

import { api } from './api';

export interface UserUsage {
  messages_used: number;
  unique_visitors: number;
  team_members: number;
  total_teams: number;  // Added to match backend
  online_agents: number;  // Added to match backend
  assigned_chatbots: number;  // Added to match backend
  conversations: number;
  api_calls: number;
  storage_mb: number;
  last_updated: string | null;
}

export interface PeriodTotals {
  messages: number;
  visitors: number;
  period: string;
}

export interface GrowthRates {
  messages: number;
  visitors: number;
}

export interface UsagePercentages {
  messages: number;
  visitors: number;
  team_members: number;
  storage: number;
  chatbots: number;
}

export interface PlanLimits {
  messages: number | null;
  visitors: number | null;
  team_members: number | null;
  storage_gb: number | null;
  chatbots: number | null;
}

export interface Organization {
  name: string;
  plan_type: string;
}

export interface DateRange {
  start: string;
  end: string;
  current_month: string;
}

export interface UserAnalyticsResponse {
  current_usage: UserUsage;
  period_totals: PeriodTotals;
  growth_rates: GrowthRates;
  usage_percentages: UsagePercentages;
  plan_limits: PlanLimits;
  organization: Organization;
  date_range: DateRange;
}

/**
 * Get user usage analytics
 */
export const getUserUsage = async (period: string = '30d'): Promise<UserAnalyticsResponse> => {
  const response = await api.get(`/user-analytics/usage?period=${period}`);
  return response.data;
};

/**
 * Format number with appropriate units
 */
export const formatUsageNumber = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
};

/**
 * Format storage size
 */
export const formatStorageSize = (mb: number): string => {
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${mb} MB`;
};

/**
 * Get usage status color
 */
export const getUsageStatusColor = (percentage: number): string => {
  if (percentage >= 90) return 'text-red-600';
  if (percentage >= 75) return 'text-orange-600';
  if (percentage >= 50) return 'text-yellow-600';
  return 'text-green-600';
};

/**
 * Get usage status background color
 */
export const getUsageStatusBgColor = (percentage: number): string => {
  if (percentage >= 90) return 'bg-red-100';
  if (percentage >= 75) return 'bg-orange-100';
  if (percentage >= 50) return 'bg-yellow-100';
  return 'bg-green-100';
};